<template>
  <v-container fluid class="legal-page pa-0">
    <section class="hero d-flex align-center justify-center">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="9" lg="8">
            <v-card class="legal-card pa-6 pa-md-8">
              <div class="text-center mb-6">
                <v-img
                  :src="require('@/assets/logo.png')"
                  contain
                  height="90"
                  class="mx-auto mb-4"
                />
                <h1 class="font-weight-bold">{{ $t('info.privacyTitle') }} - Konvenio</h1>
                <p class="effective-date">
                  Fecha de entrada en vigor: 4 de mayo de 2026 | Última actualización: 4 de mayo de 2026 | Versión: v{{ legalTermsVersion }}
                </p>
              </div>

              <div class="legal-text">
                <section v-for="section in sections" :key="section.title">
                  <h2>{{ section.title }}</h2>

                  <p v-for="paragraph in section.paragraphs || []" :key="paragraph">
                    {{ paragraph }}
                  </p>

                  <div
                    v-for="group in section.groups || []"
                    :key="group.title"
                    class="list-group"
                  >
                    <h3>{{ group.title }}</h3>
                    <ul>
                      <li v-for="item in group.items" :key="item">{{ item }}</li>
                    </ul>
                  </div>

                  <ul v-if="section.items">
                    <li v-for="item in section.items" :key="item">{{ item }}</li>
                  </ul>

                  <v-simple-table v-if="section.table" class="legal-table">
                    <thead>
                      <tr>
                        <th v-for="header in section.table.headers" :key="header">
                          {{ header }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in section.table.rows" :key="row.join('|')">
                        <td v-for="cell in row" :key="cell">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </section>

                <section>
                  <h2>23. Contacto</h2>
                  <ul>
                    <li>
                      Sitio web:
                      <a href="https://konvenio.thevalkiria.com/" target="_blank" rel="noopener">
                        https://konvenio.thevalkiria.com/
                      </a>
                    </li>
                    <li>
                      Email:
                      <a href="mailto:valkiria.lab.apps@gmail.com">
                        valkiria.lab.apps@gmail.com
                      </a>
                    </li>
                    <li>Responsable: Sorraylla Ramos</li>
                  </ul>
                </section>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </v-container>
</template>

<script>
import { LEGAL_TERMS_VERSION } from '@/config/legal'

export default {
  name: 'PrivacyPolicy',
  computed: {
    legalTermsVersion() {
      return LEGAL_TERMS_VERSION
    }
  },
  data () {
    return {
      sections: [
        {
          title: '1. Introducción',
          paragraphs: [
            'En Konvenio, accesible desde https://konvenio.thevalkiria.com/, respetamos tu privacidad y nos comprometemos a proteger tus datos personales.',
            'Esta Política de Privacidad explica qué datos recopilamos, con qué finalidad los usamos, cómo los almacenamos y protegemos, con quién podemos compartirlos y qué derechos tienes sobre tu información personal.',
            'Esta política se aplica al uso de la parte web de administración y a la aplicación de consulta para trabajadores.',
            'Esta Política de Privacidad se ha elaborado de conformidad con el Reglamento (UE) 2016/679, General de Protección de Datos -RGPD-, y la Ley Orgánica 3/2018, de Protección de Datos Personales y garantía de los derechos digitales -LOPDGDD-.',
            'Para preguntas o para ejercer derechos de privacidad, puedes contactar con nosotros en: valkiria.lab.apps@gmail.com.'
          ]
        },
        {
          title: '2. Responsable del Tratamiento',
          paragraphs: [
            'El responsable del tratamiento de los datos personales recogidos a través de Konvenio es:',
            'Responsable: Sorraylla Ramos',
            'Correo electrónico: valkiria.lab.apps@gmail.com',
            'Sitio web: https://konvenio.thevalkiria.com/',
            'País: España'
          ]
        },
        {
          title: '3. Descripción del Servicio',
          paragraphs: [
            'Konvenio es una app y web destinada a facilitar la gestión y consulta de información relacionada con convenios, avisos, calendarios laborales, documentos y comunicaciones internas de una empresa.',
            'La parte web está orientada al gestor o administrador, que puede subir información del convenio, publicar avisos, actualizar el calendario laboral, gestionar trabajadores asociados a la empresa y administrar documentos o información relacionada.',
            'La aplicación está orientada a trabajadores y tiene finalidad principalmente consultiva, permitiendo consultar la información disponible de la empresa o convenio al que estén asociados.'
          ]
        },
        {
          title: '4. Datos que Recopilamos',
          paragraphs: [
            'Konvenio recopila únicamente los datos necesarios para prestar, mantener, proteger y mejorar el servicio. Los datos tratados pueden incluir:',
            'No solicitamos ni almacenamos contraseñas de Google. Cuando se utiliza inicio de sesión con Google, la autenticación se realiza mediante Firebase Authentication y Google como proveedor de identidad. En el inicio de sesión con email y contraseña, las credenciales son gestionadas por Firebase Authentication.'
          ],
          groups: [
            {
              title: 'Datos de trabajadores o usuarios',
              items: [
                'Nombre.',
                'Dirección de correo electrónico.',
                'Empresa a la que pertenece o solicita pertenecer.',
                'Identificador de usuario generado por Firebase.',
                'Estado de aceptación o rechazo dentro de una empresa por parte del gestor.',
                'Datos técnicos y de uso asociados al acceso a la app o web.'
              ]
            },
            {
              title: 'Datos de empresa, gestor o entidad',
              items: [
                'Nombre o identificación de la empresa.',
                'Dirección.',
                'DNI, NIF, CIF u otros identificadores fiscales o administrativos.',
                'Documentos subidos al servicio.',
                'Contratos, acuerdos, convenios, anexos o documentación laboral relacionada.',
                'Avisos, mensajes, comentarios o comunicaciones introducidas por el gestor.',
                'Calendario laboral, fechas, festivos, eventos o información organizativa.'
              ]
            },
            {
              title: 'Datos técnicos y de uso',
              items: [
                'Tipo de dispositivo.',
                'Sistema operativo.',
                'Idioma.',
                'Navegador.',
                'Eventos de uso e interacciones dentro del servicio.',
                'Datos técnicos similares recogidos mediante Firebase Analytics.'
              ]
            }
          ]
        },
        {
          title: '5. Datos Sensibles',
          paragraphs: [
            'Konvenio no solicita categorías especiales de datos personales, como datos de salud, ideología, religión, orientación sexual, afiliación sindical, datos biométricos o datos genéticos.',
            'El gestor y los usuarios deben evitar introducir este tipo de información en documentos, avisos, comentarios, calendarios o cualquier otro campo del servicio, salvo que sea estrictamente necesario y exista una base legal válida para hacerlo.',
            'Dado que Konvenio puede contener información laboral o de empresa, el gestor es responsable de no subir documentación innecesaria, excesiva o que contenga datos personales de terceros sin legitimación suficiente.'
          ]
        },
        {
          title: '6. Finalidad del Tratamiento',
          table: {
            headers: ['Finalidad', 'Descripción', 'Base jurídica'],
            rows: [
              ['Autenticación y acceso', 'Permitir el acceso al servicio mediante Google, email y contraseña u otros métodos compatibles con Firebase Authentication.', 'Ejecución del servicio solicitado / consentimiento.'],
              ['Gestión de empresas y trabajadores', 'Permitir al gestor administrar trabajadores, revisar solicitudes de acceso, aceptar o rechazar pertenencia a una empresa y mantener la información asociada.', 'Ejecución del servicio solicitado / interés legítimo.'],
              ['Consulta de información laboral', 'Permitir a trabajadores consultar convenio, avisos, documentos, calendario laboral y otra información publicada por la empresa o gestor.', 'Ejecución del servicio solicitado.'],
              ['Gestión documental', 'Almacenar y mostrar documentos, contratos, acuerdos, convenios, anexos o información laboral subida por el gestor.', 'Ejecución del servicio solicitado / interés legítimo / obligación legal cuando corresponda.'],
              ['Mensajes, avisos y comentarios', 'Publicar y consultar avisos, comunicaciones o comentarios relacionados con la empresa, convenio o calendario laboral.', 'Ejecución del servicio solicitado / interés legítimo.'],
              ['Notificaciones', 'Enviar notificaciones push o comunicaciones relacionadas con avisos, cambios, documentos, calendario laboral, seguridad o actividad relevante.', 'Ejecución del servicio solicitado / interés legítimo / consentimiento cuando sea necesario.'],
              ['Mantenimiento y mejora', 'Analizar el funcionamiento de Konvenio, detectar errores, mejorar funciones y optimizar la experiencia de usuario.', 'Interés legítimo / consentimiento cuando sea necesario.'],
              ['Seguridad', 'Proteger cuentas, prevenir abusos, detectar actividad sospechosa y mantener la integridad del servicio.', 'Interés legítimo / obligación legal.'],
              ['Cumplimiento legal', 'Atender obligaciones legales, requerimientos de autoridades o procedimientos aplicables.', 'Obligación legal.']
            ]
          }
        },
        {
          title: '7. Servicios de Firebase Utilizados',
          paragraphs: [
            'Konvenio utiliza servicios de Firebase, una plataforma proporcionada por Google, para alojar, autenticar, almacenar, ejecutar funciones, enviar notificaciones y analizar el uso del servicio.',
            'Google Firebase actúa como proveedor tecnológico y, en determinados servicios, como encargado del tratamiento, conforme a sus términos de procesamiento de datos y seguridad aplicables.'
          ],
          items: [
            'Firebase Hosting: alojamiento del sitio web y entrega segura del contenido.',
            'Firebase Authentication: gestión del inicio de sesión mediante Google, email y contraseña.',
            'Cloud Firestore: almacenamiento de datos de usuarios, empresas, trabajadores, avisos, calendarios, documentos, relaciones y permisos.',
            'Firebase Analytics: medición agregada del uso del servicio.',
            'Cloud Functions: ejecución de funciones de backend necesarias para automatizaciones o lógica del servicio.',
            'Firebase Cloud Messaging: envío de notificaciones push relacionadas con el servicio.'
          ]
        },
        {
          title: '8. Inicio de Sesión y Gestión de Usuarios',
          paragraphs: [
            'Los usuarios pueden iniciar sesión mediante cuenta de Google o mediante email y contraseña, según las opciones disponibles en el servicio.',
            'Cuando se utiliza Google, recibimos únicamente la información necesaria para identificar la cuenta dentro del servicio, normalmente nombre, dirección de correo electrónico e identificador único de usuario.',
            'El gestor de una empresa puede aceptar o rechazar trabajadores asociados a dicha empresa. Los trabajadores aceptados podrán consultar la información publicada para su empresa o convenio.'
          ]
        },
        {
          title: '9. Documentos, Avisos, Calendarios y Comentarios',
          paragraphs: [
            'El gestor puede subir documentos, publicar avisos, actualizar calendarios laborales y añadir información relacionada con el convenio o la empresa.',
            'Los trabajadores podrán consultar la información que corresponda a su empresa, según los permisos y funciones disponibles.',
            'El gestor es responsable de garantizar que la información y documentación subida al servicio es lícita, adecuada, pertinente y limitada a lo necesario.'
          ]
        },
        {
          title: '10. Notificaciones',
          paragraphs: [
            'Konvenio puede enviar notificaciones push o comunicaciones relacionadas con el funcionamiento del servicio, por ejemplo:',
            'Cuando la ley lo exija, solicitaremos consentimiento para recibir determinados tipos de notificaciones. Puedes gestionar o desactivar notificaciones desde la configuración del dispositivo, navegador o aplicación, cuando esté disponible.'
          ],
          items: [
            'Nuevos avisos publicados.',
            'Actualizaciones de calendario laboral.',
            'Nuevos documentos disponibles.',
            'Cambios relevantes de empresa o convenio.',
            'Avisos técnicos o de seguridad.',
            'Actividad relacionada con solicitudes de trabajadores.'
          ]
        },
        {
          title: '11. Analytics y Medición de Uso',
          paragraphs: [
            'Utilizamos Firebase Analytics para obtener información agregada sobre el uso de Konvenio. Esta información ayuda a entender cómo se utiliza el servicio, detectar errores, mejorar funciones y optimizar la experiencia de usuario.',
            'No utilizamos Firebase Analytics para vender datos personales ni para tomar decisiones automatizadas con efectos jurídicos o significativamente similares sobre los usuarios.',
            'Las tecnologías analíticas no esenciales se utilizarán únicamente cuando sea legalmente aplicable y, cuando sea necesario, tras obtener el consentimiento del usuario.'
          ]
        },
        {
          title: '12. Cookies y Tecnologías Similares',
          paragraphs: [
            'Konvenio puede utilizar cookies, almacenamiento local u otras tecnologías similares necesarias para el funcionamiento del servicio, autenticación, seguridad, preferencias y análisis de uso.',
            'Las cookies o tecnologías analíticas no esenciales no se activarán sin consentimiento cuando así lo exija la normativa aplicable. Para más información, consulta la Política de Cookies.'
          ]
        },
        {
          title: '13. Cómo Compartimos tus Datos',
          paragraphs: [
            'No vendemos tus datos personales. Podemos compartir o permitir el acceso a datos personales únicamente en los siguientes casos:'
          ],
          items: [
            'Proveedores de servicios, como Google Firebase, que proporciona infraestructura, autenticación, base de datos, funciones, hosting, notificaciones y analytics.',
            'Gestores o administradores de empresa, cuando sea necesario para gestionar trabajadores, solicitudes y acceso a información de la empresa.',
            'Trabajadores asociados a una empresa, únicamente respecto a la información que deba estar disponible para consulta según las funciones del servicio.',
            'Obligaciones legales, cuando sea necesario cumplir con una ley, requerimiento judicial, autoridad competente o procedimiento legal.',
            'Seguridad, para proteger derechos, seguridad e integridad de Konvenio, sus usuarios o terceros.',
            'Con consentimiento, cuando el usuario autorice expresamente una comunicación para una finalidad concreta.'
          ]
        },
        {
          title: '14. Conservación de los Datos',
          paragraphs: [
            'Cuando se elimine una cuenta o se solicite la supresión de datos, eliminaremos o anonimizaremos la información asociada, salvo que exista una obligación legal o una razón legítima para conservarla.'
          ],
          table: {
            headers: ['Tipo de dato', 'Periodo de conservación', 'Motivo'],
            rows: [
              ['Datos de cuenta', 'Mientras la cuenta permanezca activa.', 'Permitir el acceso y uso de Konvenio.'],
              ['Datos de trabajadores y empresa', 'Mientras exista la relación con la empresa o sean necesarios para el servicio.', 'Gestión de usuarios, empresa y permisos.'],
              ['Documentos, contratos, acuerdos y convenios', 'Mientras sean necesarios para el servicio o hasta que el gestor los elimine, salvo obligación legal de conservación.', 'Gestión documental y consulta laboral.'],
              ['Avisos, mensajes y comentarios', 'Mientras sean necesarios para la finalidad para la que fueron publicados o hasta su eliminación.', 'Comunicación interna y consulta.'],
              ['Calendario laboral', 'Mientras sea necesario para la consulta o gestión de la empresa.', 'Organización y consulta laboral.'],
              ['Datos técnicos y analytics', 'Durante el periodo configurado en Firebase/Google Analytics.', 'Análisis, mantenimiento y mejora del servicio.']
            ]
          }
        },
        {
          title: '15. Eliminación de Cuenta y Datos Personales',
          paragraphs: [
            'Puedes solicitar la eliminación de tu cuenta y datos personales escribiendo a valkiria.lab.apps@gmail.com con el asunto "Konvenio - Eliminación de cuenta".',
            'Si la aplicación incorpora una opción de eliminación de cuenta desde ajustes, también podrá utilizarse directamente.',
            'Podremos pedir información adicional para verificar la identidad antes de proceder. La eliminación puede afectar al acceso a documentos, avisos, calendarios y demás información asociada.'
          ]
        },
        {
          title: '16. Seguridad de los Datos',
          paragraphs: [
            'Aplicamos medidas técnicas y organizativas razonables para proteger los datos personales frente a accesos no autorizados, pérdida, alteración, divulgación o destrucción.',
            'Aunque aplicamos medidas de seguridad adecuadas, ningún sistema conectado a Internet puede garantizar una seguridad absoluta.'
          ],
          items: [
            'Autenticación mediante Firebase Authentication.',
            'Reglas de seguridad de Cloud Firestore.',
            'Conexiones seguras mediante HTTPS/TLS.',
            'Control de acceso según usuario, empresa, rol o permisos.',
            'Infraestructura proporcionada por Google Firebase.',
            'Funciones de backend controladas mediante Cloud Functions.',
            'Medidas para prevenir accesos indebidos o usos abusivos.'
          ]
        },
        {
          title: '17. Transferencias Internacionales de Datos',
          paragraphs: [
            'Al utilizar servicios de Google Firebase, tus datos pueden ser tratados en servidores ubicados fuera de tu país de residencia, incluyendo países situados fuera del Espacio Económico Europeo.',
            'Cuando sea aplicable, estas transferencias se realizan conforme a mecanismos legalmente reconocidos, como cláusulas contractuales tipo, decisiones de adecuación u otras garantías previstas por la normativa de protección de datos.',
            'Puedes solicitar información adicional sobre las garantías aplicadas contactando con nosotros en valkiria.lab.apps@gmail.com.'
          ]
        },
        {
          title: '18. Tus Derechos de Privacidad',
          paragraphs: [
            'Dependiendo de tu lugar de residencia, puedes tener derecho a:',
            'Puedes ejercer estos derechos contactando en valkiria.lab.apps@gmail.com. Podremos pedir información adicional para verificar tu identidad y responderemos dentro de los plazos establecidos por la legislación aplicable.'
          ],
          items: [
            'Acceder a tus datos personales.',
            'Solicitar la rectificación de datos inexactos o incompletos.',
            'Solicitar la eliminación de tus datos.',
            'Solicitar la limitación del tratamiento.',
            'Oponerte a determinados tratamientos.',
            'Solicitar la portabilidad de tus datos.',
            'Retirar tu consentimiento cuando el tratamiento se base en consentimiento.',
            'Presentar una reclamación ante la autoridad de protección de datos competente.'
          ]
        },
        {
          title: '19. Usuarios en la Unión Europea y Espacio Económico Europeo',
          paragraphs: [
            'Si te encuentras en la Unión Europea o en el Espacio Económico Europeo, el tratamiento de tus datos personales se rige por el RGPD y, en España, por la LOPDGDD.',
            'Las bases jurídicas principales son ejecución del servicio solicitado, consentimiento, interés legítimo y obligación legal, según la finalidad concreta del tratamiento.',
            'También tienes derecho a presentar una reclamación ante la autoridad de protección de datos de tu país. En España, puedes presentar una reclamación ante la Agencia Española de Protección de Datos -AEPD-.'
          ]
        },
        {
          title: '20. Privacidad de Menores',
          paragraphs: [
            'Konvenio no está dirigido a menores de 14 años en España ni a menores de la edad mínima equivalente en otras jurisdicciones, salvo consentimiento válido de sus representantes legales cuando sea aplicable.',
            'Si crees que un menor nos ha proporcionado datos personales sin autorización, contacta con nosotros en valkiria.lab.apps@gmail.com para revisar y eliminar la información correspondiente si procede.'
          ]
        },
        {
          title: '21. Enlaces a Sitios de Terceros',
          paragraphs: [
            'Konvenio puede contener enlaces a sitios web o servicios de terceros. No somos responsables de las prácticas de privacidad, contenido o seguridad de dichos sitios externos.'
          ]
        },
        {
          title: '22. Cambios en esta Política de Privacidad',
          paragraphs: [
            'Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios legales, técnicos o funcionales. Cuando realicemos cambios importantes, actualizaremos la fecha de "Última actualización" y, si corresponde, mostraremos un aviso destacado en el servicio.'
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #7b2ff7, #ffffff);
}

.hero {
  min-height: 100vh;
  padding: 32px 0;
}

.legal-card {
  border-radius: 10px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.25);
}

.effective-date {
  color: #6f6a7a;
  font-weight: 600;
}

.legal-text section {
  margin-top: 26px;
}

.legal-text p,
.legal-text li,
.legal-text td {
  line-height: 1.7;
  color: #444;
}

.legal-text p {
  margin-bottom: 14px;
}

.legal-text h2 {
  margin-bottom: 10px;
  font-size: 1.35rem;
  font-weight: 700;
  color: #242235;
}

.legal-text h3 {
  margin-top: 16px;
  margin-bottom: 8px;
  font-size: 1rem;
  font-weight: 700;
  color: #3f3d56;
}

.legal-text ul {
  padding-left: 22px;
  margin-bottom: 14px;
}

.legal-text a {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
}

.legal-text a:hover {
  text-decoration: underline;
}

.legal-table {
  margin: 12px 0 22px;
  border: 1px solid #ece8f5;
}

.legal-table th {
  color: #242235 !important;
  font-weight: 800;
  white-space: nowrap;
}

.legal-table td,
.legal-table th {
  vertical-align: top;
}

@media (max-width: 600px) {
  .legal-table {
    display: block;
    overflow-x: auto;
  }
}
</style>
